import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mail, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errMsg = (e) => e.response?.data?.detail || "Une erreur est survenue";

export const FollowupDialog = ({ lead, onSent }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setOpen(true);
    setDraft(null);
    setBusy(true);
    try {
      setDraft((await axios.post(`${API}/admin/leads/${lead.id}/followup-draft`)).data);
    } catch (e) {
      toast.error(errMsg(e));
      setOpen(false);
    }
    setBusy(false);
  };

  const send = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/leads/${lead.id}/followup-send`, { subject: draft.subject, body: draft.body });
      toast.success("Email de relance envoyé");
      setOpen(false);
      onSent();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  if (!lead.contact?.email) return null;
  return (
    <>
      <Button variant="ghost" size="sm" onClick={generate} data-testid={`calc-lead-followup-${lead.id}`}>
        <Mail className="w-4 h-4 mr-1" /> Relance IA
        {lead.followup_sent_at && lead.followup_sent_at !== "en_cours" && <span className="ml-1 text-xs text-slate-400" data-testid={`calc-lead-followup-date-${lead.id}`}>({lead.followup_auto ? "relance auto" : "envoyée"} le {new Date(lead.followup_sent_at).toLocaleDateString("fr-FR")})</span>}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="followup-dialog">
          <DialogHeader>
            <DialogTitle>Email de relance</DialogTitle>
            <DialogDescription>À : {lead.contact.email} — relisez et modifiez avant l'envoi.</DialogDescription>
          </DialogHeader>
          {!draft ? (
            <div className="py-10 flex justify-center" data-testid="followup-loading"><Loader2 className="w-6 h-6 animate-spin text-[#102692]" /></div>
          ) : (
            <div className="space-y-3">
              <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid="followup-subject-input" />
              <Textarea rows={10} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="followup-body-input" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={generate} disabled={busy} data-testid="followup-regenerate-btn">Réécrire</Button>
                <Button onClick={send} disabled={busy} className="bg-[#102692] text-white" data-testid="followup-send-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Envoyer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
